import { NgClass } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    DestroyRef,
    HostListener,
    QueryList,
    Type,
    ViewChildren,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FocusTrapDirective } from 'shared/directives/focus-trap.directive';
import { ModalOptions } from 'shared/interfaces/modal-options.interface';
import { EventBusService } from 'shared/services';
import { filter, Observable, of, Subject, take } from 'rxjs';
import isFunction from 'lodash-es/isFunction';

const defaultModalOptions: ModalOptions = {
    closeOnEscape: true,
    closeOnEscapeResult: null,
    closeOnOverlayClick: true,
    closeOnOverlayClickResult: null,
};

@Component({
    selector: 'app-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    imports: [NgClass, FocusTrapDirective],
})
export class ModalComponent {
    @ViewChildren('modalContentContainer', { read: ViewContainerRef }) modalList!: QueryList<any>;

    @HostListener('document:keydown.escape', []) onKeydownHandler() {
        const lastModalIndex = this.modals.length - 1;

        if (lastModalIndex < 0) {
            return;
        }

        if (this.modals[lastModalIndex].options.closeOnEscape) {
            this.closeModal(lastModalIndex, this.modals[lastModalIndex].options.closeOnEscapeResult);
        }
    }

    public modals: {
        data: any;
        componentRef?: ComponentRef<any>;
        modalCloseSubject: Subject<any>;
        componentType: Type<any>;
        options: ModalOptions;
    }[] = [];

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private eventBus: EventBusService,
        private destroyRef: DestroyRef,
    ) {}

    public openModal<T>(
        component: Type<T>,
        data?: {
            type: 'top' | 'center' | 'side';
            [key: string]: any;
        },
        options?: ModalOptions,
    ): {
        onClose: Observable<unknown>;
        index: number;
        componentInstance: T;
    } {
        let finalOptions = defaultModalOptions;

        if (options) {
            finalOptions = {
                ...finalOptions,
                ...options,
            };
        }

        this.modals.push({
            data: data,
            modalCloseSubject: new Subject(),
            componentType: component,
            options: finalOptions,
        });

        // We need to wait for Angular to update the view and add the new elements to the modal list
        this.changeDetectorRef.detectChanges();

        const lastModalIndex = this.modals.length - 1;
        const componentRef = this.modalList.get(lastModalIndex).createComponent(component);
        this.modals[lastModalIndex].componentRef = componentRef;

        componentRef.instance.data = data;
        componentRef.instance.closeModalInstance = this.closeModal.bind(this, lastModalIndex);

        this.eventBus.dispatch('modalOpen', true);

        return {
            onClose: this.modals[lastModalIndex].modalCloseSubject.asObservable().pipe(take(1)),
            index: lastModalIndex,
            componentInstance: componentRef.instance,
        };
    }

    public closeModal(index: number, result?: unknown, forceClose: boolean = false) {
        if (!forceClose) {
            this.canCloseModal(index)
                .pipe(take(1), takeUntilDestroyed(this.destroyRef), filter(Boolean))
                .subscribe(() => this.closeModal(index, result, true));
            return;
        }

        const currentModalLength = this.modals.length;
        this.modals[index].modalCloseSubject.next(result);
        const newModalLength = this.modals.length;

        if (currentModalLength !== newModalLength) {
            index = index - (currentModalLength - newModalLength);
        }
        this.modals[index]?.modalCloseSubject.complete(); // We do this to avoid memory leaks
        this.modals.splice(index, 1);
    }

    public closeModalByOverlay(index: number) {
        if (this.modals[index].options.closeOnOverlayClick) {
            this.closeModal(index, this.modals[index].options.closeOnOverlayClickResult);
        }
    }

    public closeAllModals(result?: unknown) {
        this.modals.forEach((modal, index) => {
            this.closeModal(index, result, true);
        });

        this.eventBus.dispatch('modalOpen', undefined);
    }

    private canCloseModal(index: number): Observable<boolean> {
        // Check if the modal can be closed using the canDeactivateTab method
        if (this.modals[index].componentRef?.instance && isFunction(this.modals[index].componentRef!.instance.canDeactivateModal)) {
            const result = this.modals[index].componentRef!.instance.canDeactivateModal();

            if (result instanceof Observable || result instanceof Subject) {
                return result;
            } else if (typeof result === 'boolean') {
                return of(result);
            }
        }

        return of(true);
    }
}
